import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  TextField,
  Grid,
  Divider
} from '@mui/material';
import {
  Fingerprint as FingerprintIcon,
  PhoneAndroid as PhoneIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import api from '../../utils/api';
import WebAuthnSetup from '../WebAuthn/WebAuthnSetup';
import TOTPSetup from '../TOTP/TOTPSetup';
import { validatePassword } from '../../utils/passwordValidation';

interface MfaManagementTabProps {
  user: any;
  onError: (error: string) => void;
  onSuccess: (message: string) => void;
}

interface WebAuthnCredential {
  id: string;
  name: string;
  createdAt: string;
  lastUsed?: string;
  transports?: string[];
  credentialDeviceType: 'singleDevice' | 'multiDevice';
}

function MfaManagementTab({ user, onError, onSuccess }: MfaManagementTabProps) {
  const [webAuthnCredentials, setWebAuthnCredentials] = useState<WebAuthnCredential[]>([]);
  const [totpEnabled, setTotpEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [addWebAuthnDialog, setAddWebAuthnDialog] = useState(false);
  const [addTotpDialog, setAddTotpDialog] = useState(false);
  const [editCredentialDialog, setEditCredentialDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [disableTotpDialog, setDisableTotpDialog] = useState(false);
  const [changePasswordDialog, setChangePasswordDialog] = useState(false);
  
  // Selected credential for editing/deleting
  const [selectedCredential, setSelectedCredential] = useState<WebAuthnCredential | null>(null);
  const [newCredentialName, setNewCredentialName] = useState('');
  
  // Password change states
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordChanging, setPasswordChanging] = useState(false);

  const loadMfaMethods = useCallback(async () => {
    try {
      setLoading(true);
      
      // Load WebAuthn credentials
      const webAuthnResponse = await api.get('/api/webauthn/credentials');
      if (webAuthnResponse.data.success) {
        setWebAuthnCredentials(webAuthnResponse.data.credentials);
      }
      
      // Load TOTP status
      const totpResponse = await api.get('/api/totp/status');
      if (totpResponse.data.success) {
        setTotpEnabled(totpResponse.data.totpEnabled);
      }
    } catch (error: any) {
      console.error('Error loading MFA methods:', error);
      onError('Failed to load MFA methods');
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    loadMfaMethods();
  }, [loadMfaMethods]);

  const handleAddWebAuthn = () => {
    setAddWebAuthnDialog(true);
  };

  const handleWebAuthnSetupComplete = async () => {
    setAddWebAuthnDialog(false);
    await loadMfaMethods();
    onSuccess('WebAuthn credential added successfully');
  };

  const handleAddTotp = () => {
    setAddTotpDialog(true);
  };

  const handleTotpSetupComplete = async () => {
    setAddTotpDialog(false);
    await loadMfaMethods();
    onSuccess('TOTP authenticator added successfully');
  };

  const handleEditCredential = (credential: WebAuthnCredential) => {
    setSelectedCredential(credential);
    setNewCredentialName(credential.name);
    setEditCredentialDialog(true);
  };

  const handleSaveCredentialName = async () => {
    if (!selectedCredential) return;

    try {
      const response = await api.put(`/api/webauthn/credentials/${selectedCredential.id}`, {
        name: newCredentialName
      });

      if (response.data.success) {
        await loadMfaMethods();
        onSuccess('Credential name updated successfully');
        setEditCredentialDialog(false);
      }
    } catch (error: any) {
      onError(error.response?.data?.error || 'Failed to update credential name');
    }
  };

  const handleDeleteCredential = (credential: WebAuthnCredential) => {
    setSelectedCredential(credential);
    setDeleteDialog(true);
  };

  const confirmDeleteCredential = async () => {
    if (!selectedCredential) return;

    try {
      const response = await api.delete(`/api/webauthn/credentials/${selectedCredential.id}`);

      if (response.data.success) {
        await loadMfaMethods();
        onSuccess('Credential removed successfully');
        setDeleteDialog(false);
      }
    } catch (error: any) {
      onError(error.response?.data?.error || 'Failed to remove credential');
    }
  };

  const handleDisableTotp = () => {
    setDisableTotpDialog(true);
  };

  const confirmDisableTotp = async () => {
    try {
      const response = await api.delete('/api/totp/disable');

      if (response.data.success) {
        await loadMfaMethods();
        onSuccess('TOTP authenticator disabled successfully');
        setDisableTotpDialog(false);
      }
    } catch (error: any) {
      onError(error.response?.data?.error || 'Failed to disable TOTP');
    }
  };

  const handleChangePassword = async () => {
    // Validate passwords
    if (!currentPassword || !newPassword || !confirmPassword) {
      onError('Please fill in all password fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      onError('New passwords do not match');
      return;
    }

    const validation = validatePassword(newPassword);
    if (!validation.isValid) {
      onError(validation.errors.join(', '));
      return;
    }

    try {
      setPasswordChanging(true);
      await api.post('/auth/change-password', {
        currentPassword,
        newPassword
      });

      // Clear form
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setChangePasswordDialog(false);
      onSuccess('Password changed successfully');
    } catch (error: any) {
      onError(error.response?.data?.error || error.response?.data?.message || 'Failed to change password');
    } finally {
      setPasswordChanging(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const hasMfaEnabled = webAuthnCredentials.length > 0 || totpEnabled;
  const canRemoveMethod = (webAuthnCredentials.length + (totpEnabled ? 1 : 0)) > 1;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Password Change Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <LockIcon sx={{ mr: 1, color: '#6366f1' }} />
          <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>
            Password
          </Typography>
        </Box>
        <Typography variant="body2" paragraph sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
          Change your account password to keep your account secure
        </Typography>
        <Button
          variant="outlined"
          onClick={() => setChangePasswordDialog(true)}
          sx={{
            borderColor: 'rgba(255, 255, 255, 0.23)',
            color: 'rgba(255, 255, 255, 0.9)',
            '&:hover': {
              borderColor: '#6366f1',
              backgroundColor: 'rgba(99, 102, 241, 0.1)'
            }
          }}
        >
          Change Password
        </Button>
      </Box>

      <Divider sx={{ my: 4, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

      {/* MFA Section */}
      <Typography variant="h6" gutterBottom sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>
        Multi-Factor Authentication
      </Typography>
      <Typography variant="body2" paragraph sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
        Protect your account with multiple authentication methods. You can use both biometric/security keys and authenticator apps.
      </Typography>

      {!hasMfaEnabled && (
        <Alert 
          severity="warning" 
          sx={{ 
            mb: 3,
            backgroundColor: 'rgba(245, 158, 11, 0.1)',
            border: '1px solid rgba(245, 158, 11, 0.2)',
            color: '#fcd34d'
          }}
        >
          <Typography variant="body2">
            <strong>Security Warning:</strong> You must have at least one MFA method enabled to maintain account security.
          </Typography>
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* WebAuthn Section */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 2,
              p: 3
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <FingerprintIcon sx={{ mr: 1, color: '#6366f1' }} />
              <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>
                Biometric / Security Keys
              </Typography>
            </Box>
            
            <Typography variant="body2" paragraph sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              Use fingerprint, face recognition, or physical security keys
            </Typography>

              {webAuthnCredentials.length > 0 ? (
                <List dense>
                  {webAuthnCredentials.map((credential) => (
                    <ListItem 
                      key={credential.id} 
                      divider
                      sx={{
                        borderColor: 'rgba(255, 255, 255, 0.1)'
                      }}
                    >
                      <ListItemText
                        primary={credential.name}
                        secondary={
                          <>
                            <Typography variant="caption" display="block" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                              Added: {formatDate(credential.createdAt)}
                            </Typography>
                            {credential.lastUsed && (
                              <Typography variant="caption" display="block" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                Last used: {formatDate(credential.lastUsed)}
                              </Typography>
                            )}
                          </>
                        }
                        primaryTypographyProps={{
                          sx: { color: 'rgba(255, 255, 255, 0.95)', fontWeight: 500 }
                        }}
                      />
                      <ListItemSecondaryAction>
                        <IconButton 
                          edge="end" 
                          size="small" 
                          onClick={() => handleEditCredential(credential)}
                          sx={{ 
                            mr: 1,
                            color: 'rgba(255, 255, 255, 0.7)',
                            '&:hover': { color: 'rgba(255, 255, 255, 0.9)' }
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          edge="end" 
                          size="small" 
                          onClick={() => handleDeleteCredential(credential)}
                          disabled={!canRemoveMethod}
                          sx={{
                            color: 'rgba(255, 255, 255, 0.7)',
                            '&:hover': { color: '#ef4444' },
                            '&.Mui-disabled': { color: 'rgba(255, 255, 255, 0.3)' }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Alert 
                  severity="info" 
                  sx={{ 
                    mb: 2,
                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                    border: '1px solid rgba(59, 130, 246, 0.2)',
                    color: '#93c5fd'
                  }}
                >
                  No biometric credentials configured
                </Alert>
              )}

              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddWebAuthn}
                fullWidth
                sx={{ 
                  mt: 2,
                  borderColor: 'rgba(255, 255, 255, 0.23)',
                  color: 'rgba(255, 255, 255, 0.9)',
                  '&:hover': {
                    borderColor: '#6366f1',
                    backgroundColor: 'rgba(99, 102, 241, 0.1)'
                  }
                }}
              >
                Add Biometric / Security Key
              </Button>
          </Box>
        </Grid>

        {/* TOTP Section */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 2,
              p: 3
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <PhoneIcon sx={{ mr: 1, color: '#6366f1' }} />
              <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>
                Authenticator App
              </Typography>
            </Box>
            
            <Typography variant="body2" paragraph sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              Use apps like Google Authenticator, Authy, or Microsoft Authenticator
            </Typography>

              {totpEnabled ? (
                <Box>
                  <Alert 
                    severity="success" 
                    icon={<CheckCircleIcon />} 
                    sx={{ 
                      mb: 2,
                      backgroundColor: 'rgba(16, 185, 129, 0.1)',
                      border: '1px solid rgba(16, 185, 129, 0.2)',
                      color: '#6ee7b7'
                    }}
                  >
                    <Typography variant="body2">
                      TOTP authenticator is enabled
                    </Typography>
                  </Alert>
                  
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleDisableTotp}
                    fullWidth
                    disabled={!canRemoveMethod}
                    sx={{
                      borderColor: 'rgba(239, 68, 68, 0.5)',
                      color: '#fca5a5',
                      '&:hover': {
                        borderColor: '#ef4444',
                        backgroundColor: 'rgba(239, 68, 68, 0.1)'
                      },
                      '&.Mui-disabled': {
                        borderColor: 'rgba(255, 255, 255, 0.12)',
                        color: 'rgba(255, 255, 255, 0.3)'
                      }
                    }}
                  >
                    Disable Authenticator App
                  </Button>
                  
                  {!canRemoveMethod && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'rgba(255, 255, 255, 0.5)' }}>
                      Add another MFA method before disabling this one
                    </Typography>
                  )}
                </Box>
              ) : (
                <Box>
                  <Alert 
                    severity="info" 
                    sx={{ 
                      mb: 2,
                      backgroundColor: 'rgba(59, 130, 246, 0.1)',
                      border: '1px solid rgba(59, 130, 246, 0.2)',
                      color: '#93c5fd'
                    }}
                  >
                    No authenticator app configured
                  </Alert>
                  
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleAddTotp}
                    fullWidth
                    sx={{
                      borderColor: 'rgba(255, 255, 255, 0.23)',
                      color: 'rgba(255, 255, 255, 0.9)',
                      '&:hover': {
                        borderColor: '#6366f1',
                        backgroundColor: 'rgba(99, 102, 241, 0.1)'
                      }
                    }}
                  >
                    Add Authenticator App
                  </Button>
                </Box>
              )}
          </Box>
        </Grid>
      </Grid>

      {/* Add WebAuthn Dialog */}
      <Dialog 
        open={addWebAuthnDialog} 
        onClose={() => setAddWebAuthnDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(17, 24, 39, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)'
          }
        }}
      >
        <DialogTitle sx={{ color: 'rgba(255, 255, 255, 0.95)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
          Add Biometric / Security Key
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <WebAuthnSetup
            onSetupComplete={handleWebAuthnSetupComplete}
            onError={onError}
            showExisting={false}
          />
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)', px: 3, py: 2 }}>
          <Button 
            onClick={() => setAddWebAuthnDialog(false)}
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' }
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add TOTP Dialog */}
      <Dialog 
        open={addTotpDialog} 
        onClose={() => setAddTotpDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(17, 24, 39, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)'
          }
        }}
      >
        <DialogTitle sx={{ color: 'rgba(255, 255, 255, 0.95)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
          Add Authenticator App
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <TOTPSetup
            onSetupComplete={handleTotpSetupComplete}
            onError={onError}
          />
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)', px: 3, py: 2 }}>
          <Button 
            onClick={() => setAddTotpDialog(false)}
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' }
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Credential Dialog */}
      <Dialog 
        open={editCredentialDialog} 
        onClose={() => setEditCredentialDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(17, 24, 39, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)'
          }
        }}
      >
        <DialogTitle sx={{ color: 'rgba(255, 255, 255, 0.95)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
          Edit Credential Name
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <TextField
            autoFocus
            margin="dense"
            label="Credential Name"
            type="text"
            fullWidth
            value={newCredentialName}
            onChange={(e) => setNewCredentialName(e.target.value)}
            helperText="Give this credential a memorable name"
            sx={{
              '& .MuiOutlinedInput-root': {
                color: 'rgba(255, 255, 255, 0.9)',
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' },
                '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.4)' },
                '&.Mui-focused fieldset': { borderColor: '#6366f1' }
              },
              '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
              '& .MuiFormHelperText-root': { color: 'rgba(255, 255, 255, 0.5)' }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)', px: 3, py: 2 }}>
          <Button 
            onClick={() => setEditCredentialDialog(false)}
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveCredentialName} 
            variant="contained"
            sx={{
              backgroundColor: '#4f46e5',
              '&:hover': { backgroundColor: '#4338ca' }
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Credential Dialog */}
      <Dialog 
        open={deleteDialog} 
        onClose={() => setDeleteDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(17, 24, 39, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)'
          }
        }}
      >
        <DialogTitle sx={{ color: 'rgba(255, 255, 255, 0.95)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
          Remove Credential?
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert 
            severity="warning" 
            sx={{ 
              mb: 2,
              backgroundColor: 'rgba(245, 158, 11, 0.1)',
              border: '1px solid rgba(245, 158, 11, 0.2)',
              color: '#fcd34d'
            }}
          >
            Are you sure you want to remove "{selectedCredential?.name}"? This action cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)', px: 3, py: 2 }}>
          <Button 
            onClick={() => setDeleteDialog(false)}
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmDeleteCredential} 
            variant="contained"
            sx={{
              backgroundColor: '#ef4444',
              '&:hover': { backgroundColor: '#dc2626' }
            }}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* Disable TOTP Dialog */}
      <Dialog 
        open={disableTotpDialog} 
        onClose={() => setDisableTotpDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(17, 24, 39, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)'
          }
        }}
      >
        <DialogTitle sx={{ color: 'rgba(255, 255, 255, 0.95)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
          Disable Authenticator App?
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert 
            severity="warning" 
            sx={{ 
              mb: 2,
              backgroundColor: 'rgba(245, 158, 11, 0.1)',
              border: '1px solid rgba(245, 158, 11, 0.2)',
              color: '#fcd34d'
            }}
          >
            Are you sure you want to disable your authenticator app? You will need to set it up again if you want to use it in the future.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)', px: 3, py: 2 }}>
          <Button 
            onClick={() => setDisableTotpDialog(false)}
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmDisableTotp} 
            variant="contained"
            sx={{
              backgroundColor: '#ef4444',
              '&:hover': { backgroundColor: '#dc2626' }
            }}
          >
            Disable
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog 
        open={changePasswordDialog} 
        onClose={() => setChangePasswordDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(17, 24, 39, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)'
          }
        }}
      >
        <DialogTitle sx={{ color: 'rgba(255, 255, 255, 0.95)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
          Change Password
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="password"
                label="Current Password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'rgba(255, 255, 255, 0.9)',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' },
                    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.4)' },
                    '&.Mui-focused fieldset': { borderColor: '#6366f1' }
                  },
                  '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="password"
                label="New Password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                helperText="Must be at least 8 characters with uppercase, lowercase, number, and special character"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'rgba(255, 255, 255, 0.9)',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' },
                    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.4)' },
                    '&.Mui-focused fieldset': { borderColor: '#6366f1' }
                  },
                  '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                  '& .MuiFormHelperText-root': { color: 'rgba(255, 255, 255, 0.5)' }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="password"
                label="Confirm New Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'rgba(255, 255, 255, 0.9)',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' },
                    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.4)' },
                    '&.Mui-focused fieldset': { borderColor: '#6366f1' }
                  },
                  '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)', px: 3, py: 2 }}>
          <Button 
            onClick={() => {
              setChangePasswordDialog(false);
              setCurrentPassword('');
              setNewPassword('');
              setConfirmPassword('');
            }}
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleChangePassword} 
            variant="contained"
            disabled={passwordChanging}
            sx={{
              backgroundColor: '#4f46e5',
              '&:hover': { backgroundColor: '#4338ca' }
            }}
          >
            {passwordChanging ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Change Password'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default MfaManagementTab;
